import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Account } from '../../types';
import { useDeleteAccount } from '../hooks/useDeleteAccount';
import { useModalState } from '@/shared/hooks';
import { formatError } from '@/shared/api';
import RemoveAccountModal from './RemoveAccountModal';

interface Props {
  account: Account;
}

export default function DeleteAccountButton({ account }: Props) {
  const [removeError, setRemoveError] = useState<string | null>(null);
  const deleteAccount = useDeleteAccount();
  const { isOpen: isRemoveOpen, open: openRemove, close: closeRemove } = useModalState();
  const intl = useIntl();

  const handleCloseRemove = () => {
    if (deleteAccount.isPending) return;
    closeRemove();
  };

  const handleConfirmRemove = async () => {
    setRemoveError(null);
    try {
      await deleteAccount.mutateAsync(account.id);
      closeRemove();
    } catch (error_) {
      setRemoveError(formatError(error_, intl.formatMessage({ id: 'accounts.card.removeError' })));
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => { setRemoveError(null); openRemove(); }}
        disabled={deleteAccount.isPending}
        className="p-1.5 rounded text-slate-400 hover:bg-red-50 hover:text-red-600 transition disabled:opacity-50"
        aria-label={intl.formatMessage({ id: 'accounts.card.removeAccount' })}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      <RemoveAccountModal
        isOpen={isRemoveOpen}
        onClose={handleCloseRemove}
        onConfirm={handleConfirmRemove}
        isSubmitting={deleteAccount.isPending}
        error={removeError}
      />
    </>
  );
}
